import { Injectable } from '@angular/core';
import {Subscription} from 'rxjs';
import {Map, GeoJSONSource, MapMouseEvent, LngLat} from 'maplibre-gl';
import {PointFeature} from '../../../models/GeoJson';
import {ObservationMapMarkerService} from './observation-map-marker.service';
import {VehicleMapMarkerService} from './vehicle-map-marker.service';
import {VehiclesManagerService} from '../../../../data/vehicles/vehicles-manager.service';
import {ObservationsManagerService} from '../../../../data/observations/observations-manager.service';
import {ImagesManagerService} from '../../../../data/images/images-manager.service';
import {MatSnackBarRef, SimpleSnackBar} from '@angular/material/snack-bar';
import {BreadcrumbsManagerService} from '../../../../data/breadcrumbs/breadcrumbs-manager.service';
import {TracksVectorTilesLayerService} from './tracks-vector-tiles-layer.service';
import {PopupInfoService} from './popup-info.service';
import {TracksGeoJsonLayerService} from './tracks-geo-json-layer.service';
import {VehicleBreadcrumb} from '../../../models/vehicle-breadcrumb';
import {LocationSearchParams} from '../../../models/breadcrumb.model';
import {RoadStatusCoverageLayerService} from './road-status-coverage-layer.service';
import {RoadStatusCurrencyLayerService} from './road-status-currency-layer.service';
import {WeatherWarningsLayerService} from './weather-warnings-layer.service';
import {ToastService} from '../../../services/toast.service';
import { AssetsManagerService } from 'src/app/data/assets/assets-manager.service';
import {Asset} from '../../../../pages/live-map/models/asset.class';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {MapControlService} from './map-control.service';
import {ImageMapMarkerService} from './image-map-marker.service';
import {TrafficLayerService} from './traffic-layer.service';
import {MainRoute, RootRoute} from '../../../models/angular-routing';
import {RoutesLayerService} from './routes-layer.service';
import { HighlightedItemSource } from '../../../models/highlighted-item-update';
import { ConfigurationModel } from '../../../models/configuration.model';
import { ActivityFilter, SettingsService } from '../../../../configuration/settings.service';
import { LocationApiService } from '../../../../data/location-api/location-api.service';
import { BackendLocationModel } from '../../../models/location.model';
import { RouteSegmentStatusLayerService } from './route-segment-status-layer.service';
import { RoutesManagerService } from '../../../../data/routes/routes-manager.service';

@Injectable({
  providedIn: 'root'
})
export class MapEventService {

  private map: Map;
  private configuration: ConfigurationModel;
  private searchSnackbar: MatSnackBarRef<SimpleSnackBar> = null;
  private vehicleIdsFilter: number[] = null;
  private vehicleDetailMode: number;
  private defaultHoursAgoFilter = 12;
  private hoursAgoFilter = 12;
  private activityFilter: ActivityFilter = null;
  private observationPlacementModeActive = false;
  private currentRouteConfigId: number;

  private readonly handledClickEvents = new WeakSet<MouseEvent>();
  private readonly openSubscriptions = Array<Subscription>();

  constructor(private assetManager: AssetsManagerService,
              private vehiclesManager: VehiclesManagerService,
              private locationApi: LocationApiService,
              private breadcrumbManagerService: BreadcrumbsManagerService,
              private observationsManagerService: ObservationsManagerService,
              private imagesManagerService: ImagesManagerService,
              private popupInfoService: PopupInfoService,
              private routesLayerService: RoutesLayerService,
              private routesManager: RoutesManagerService,
              private routeSegmentStatusLayerService: RouteSegmentStatusLayerService,
              private weatherWarningsService: WeatherWarningsLayerService,
              private trafficLayerService: TrafficLayerService,
              private mapControlService: MapControlService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private settingsService: SettingsService,
              private toast: ToastService) { }

  init(map: Map, configuration: ConfigurationModel) {
    if (!!this.map) {
      throw Error('The map has already been set. Only a single map instance is supported.');
    }

    this.map = map;
    this.configuration = configuration;
    this.defaultHoursAgoFilter = configuration.lookBackPeriod;
    this.vehicleDetailMode =
      !!this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.routeConfig?.path
        ?.startsWith(MainRoute.VEHICLE + '/:id')
      ? this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.params?.id ?? 0
      : 0;
    const routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.vehicleDetailMode =
          !!this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.routeConfig?.path
            ?.startsWith(MainRoute.VEHICLE + '/:id')
            ? this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.params?.id ?? 0
            : 0;
      }
    });
    this.openSubscriptions.push(routerSubscription);

    const changesSubscription = this.settingsService.settingsChangedObservable.subscribe(newSettings => {
        if (newSettings.key === SettingsService.PLOW_TRACK_TIME_FILTER) {
          this.hoursAgoFilter = newSettings.value;
        } else if (newSettings.key === SettingsService.ACTIVITY_FILTER) {
          this.activityFilter = newSettings.value;
        }
    });
    this.openSubscriptions.push(changesSubscription);

    const vehiclesFilterSubscription = this.assetManager.visibleTrackAssets$.subscribe(assets => {
      this.handleAssetsChanged(assets);
    });
    this.openSubscriptions.push(vehiclesFilterSubscription);

    const routesFilterSubscription = this.routesManager.routeFilter$.subscribe(filterUpdate => {
      this.currentRouteConfigId = filterUpdate.configId;
    });
    this.openSubscriptions.push(routesFilterSubscription);

    this.attachOnClickHandlers();
  }

  release() {
    if (!this.map) {
      throw Error('The map has not been set.');
    }
    this.openSubscriptions.forEach(subscription => subscription?.unsubscribe());

    this.vehicleIdsFilter = null;
    this.map = null;
    this.configuration = null;
    this.observationPlacementModeActive = false;
  }

  setObservationPlacementMode(active: boolean) {
    if (!this.map) {
      return;
    }
    this.observationPlacementModeActive = active;
    this.map.getCanvas().style.cursor = active ? 'crosshair' : '';
  }

  attachOnClickHandlers() {
    const that = this;
    // on observation point click handler
    this.map.on('click', ObservationMapMarkerService.LAYER_ID_OBSERVATIONS_CIRCLE, (e) => {
      if (this.checkClickPropagation(e)) {
        return;
      }
      // @ts-ignore
      const pointFeature = e.features[0] as PointFeature;
      this.observationsManagerService.highlightObservation(pointFeature.properties.id, ObservationsManagerService.MAP_SOURCE);
    });
    this.showPointCursorForLayer(ObservationMapMarkerService.LAYER_ID_OBSERVATIONS_CIRCLE);

    // on vehicle point click handler
    this.map.on('click', VehicleMapMarkerService.LAYER_ID_VEHICLES_CIRCLE, (e) => {
      // console.log('vehicle marker click');
      if (this.checkClickPropagation(e)) {
        return;
      }
      // @ts-ignore
      const pointFeature = e.features[0] as PointFeature;
      this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.VEHICLE, pointFeature.properties.id], {
        queryParams: {
          expandVehicleGroup: true,
        },
        queryParamsHandling: 'merge',
      });
    });
    this.showPointCursorForLayer(VehicleMapMarkerService.LAYER_ID_VEHICLES_CIRCLE);

    // on vehicle label click handler
    this.map.on('click', VehicleMapMarkerService.LAYER_ID_VEHICLES_LABEL, (e) => {
      // console.log('vehicle marker click');
      if (this.checkClickPropagation(e)) {
        return;
      }
      // @ts-ignore
      const pointFeature = e.features[0] as PointFeature;
      this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.VEHICLE, pointFeature.properties.id], {
        queryParams: {
          expandVehicleGroup: true,
        },
        queryParamsHandling: 'merge',
      });
    });
    this.showPointCursorForLayer(VehicleMapMarkerService.LAYER_ID_VEHICLES_LABEL);

    // on image point click handler
    this.map.on('click', ImageMapMarkerService.LAYER_ID_IMAGES_CIRCLE, (e) => {
      if (this.checkClickPropagation(e)) {
        return;
      }
      // @ts-ignore
      const pointFeature = e.features[0] as PointFeature;
      this.imagesManagerService.highlightImage(pointFeature.properties.id, HighlightedItemSource.MAP);
    });
    this.showPointCursorForLayer(ImageMapMarkerService.LAYER_ID_IMAGES_CIRCLE);

    // road status layers
    for (const i of [1, 2, 3, 4, 5]) {
      that.map.on('click', `${RoadStatusCoverageLayerService.LAYER_ID_PREFIX}-${i}`, (e) => {
        this.handleRoadStatusLayerClick(e);
      });
      this.showPointCursorForLayer(`${RoadStatusCoverageLayerService.LAYER_ID_PREFIX}-${i}`);

      that.map.on('click', `${RoadStatusCurrencyLayerService.LAYER_ID_PREFIX}-${i}`, (e) => {
        this.handleRoadStatusLayerClick(e);
      });
      this.showPointCursorForLayer(`${RoadStatusCurrencyLayerService.LAYER_ID_PREFIX}-${i}`);
    }

    // on breadcrumb search
    that.map.on('click', TracksVectorTilesLayerService.LAYER_ID_ACTIVE_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, true);
    });
    this.showPointCursorForLayer(TracksVectorTilesLayerService.LAYER_ID_ACTIVE_PADDING);
    that.map.on('click', TracksVectorTilesLayerService.LAYER_ID_HISTORY_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, false);
    });
    this.showPointCursorForLayer(TracksVectorTilesLayerService.LAYER_ID_HISTORY_PADDING);
    that.map.on('click', TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, true);
    });
    this.showPointCursorForLayer(TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON_PADDING);
    that.map.on('click', TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, false);
    });
    this.showPointCursorForLayer(TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON_PADDING);
    that.map.on('click', TracksVectorTilesLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, true);
    });
    this.showPointCursorForLayer(TracksVectorTilesLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING);
    that.map.on('click', TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING, (e) => {
      that.handleBreadCrumbSearchClick(e, true);
    });
    this.showPointCursorForLayer(TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING);

    // on route click - assignment status theme layers
    const routes = this.routesLayerService.getRoutes();
    this.routesLayerService.getRouteConfigurationLayers()?.forEach(routeConfigurationLayer => {
      this.map.on('click', routeConfigurationLayer.id, (e) => {
        if (this.checkClickPropagation(e)) {
          return;
        }
        // @ts-ignore
        const pointFeature = e.features[0] as PointFeature;
        const route = routes.find(r => {
          // @ts-ignore
          return r.configId === pointFeature.properties.configid && r.routeId === pointFeature.properties.routeid;
        });
        const routePath = !!route.path ? route.path : [];
        const path = [String(route.configId), ...routePath, route.value];
        this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.ROUTE, path.join(':::'), 'route-id', route.routeId], {
          queryParamsHandling: 'merge',
          queryParams: {
            expandRouteGroup: true,
          }
        });
      });
      this.map.on('mouseenter', routeConfigurationLayer.id, (e) => {
        if (this.checkPropagation(e)) {
          return;
        }
        // @ts-ignore
        const pointFeature = e.features[0] as PointFeature;
        // @ts-ignore
        this.routesLayerService.highlightRoute(pointFeature.properties.configid, pointFeature.properties.routeid);
      });
      this.map.on('mouseleave', routeConfigurationLayer.id, (e) => {
        if (this.checkPropagation(e)) {
          return;
        }
        this.routesLayerService.highlightRoute(undefined, undefined);
      });
      this.showPointCursorForLayer(routeConfigurationLayer.id);
    });

    // on route status layers click
    for (const i of [0, 1, 2, 3]) {
      this.map.on('click', `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${i}`, e => {
        if (this.checkClickPropagation(e)) {
          return;
        }
        // @ts-ignore
        const pointFeature = e.features[0] as PointFeature;
        if (this.currentRouteConfigId) {
          const route = routes.find(r => {
            // @ts-ignore
            return r.configId === this.currentRouteConfigId && r.routeId === pointFeature.properties.routeid;
          });
          if (route) {
            const routePath = !!route.path ? route.path : [];
            const path = [String(route.configId), ...routePath, route.value];
            this.router.navigate([`/${RootRoute.MAIN}`, MainRoute.ROUTE, path.join(':::'), 'route-id', route.routeId], {
              queryParamsHandling: 'merge',
              queryParams: {
                expandRouteGroup: true,
              },
            });
          }
        } else {
          console.warn('Invalid Route Configuration ID filter when clicking on Route Status layer.')
        }
      });
      this.map.on('mouseenter', `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${i}`, e => {
        if (this.checkPropagation(e)) {
          return;
        }
        // @ts-ignore
        const pointFeature = e.features[0] as PointFeature;
        // @ts-ignore
        this.routeSegmentStatusLayerService.highlightRoute(this.currentRouteConfigId, pointFeature.properties.routeid);
      });
      this.map.on('mouseleave', `${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${i}`, e => {
        if (this.checkPropagation(e)) {
          return;
        }
        this.routeSegmentStatusLayerService.highlightRoute(undefined, undefined);
      });
      this.showPointCursorForLayer(`${RouteSegmentStatusLayerService.LAYER_ID_PREFIX}-${i}`);
    }

    // on default map click (nothing from PlowOps is clicked)
    this.map.on('click', (e) => {
      if (this.checkClickPropagation(e)) {
        return;
      }
      // deselect observation
      this.observationsManagerService.highlightObservation(null, ObservationsManagerService.MAP_SOURCE);
      // deselect image
      this.imagesManagerService.highlightImage(null, HighlightedItemSource.MAP);
      // close map layer switcher
      this.mapControlService.toggleMapLayerSwitcher(false);
      // show popup on Weather Warnings layer
      if (this.weatherWarningsService.isVisible()) {
        this.weatherWarningsService.getWmsFeatures(e.lngLat).then(featureCollection => {
          if (featureCollection.features.length === 0) {
            return;
          }
          const feature = featureCollection.features[0];
          this.popupInfoService.showWeatherAlert(e.lngLat, feature.properties.cap_id, feature.properties.prod_type);
        });
      }

      // show popup on Traffic layer
      if (this.trafficLayerService.isVisible()) {
        this.trafficLayerService.queryIncidents(this.map, e.lngLat).then(json => {
          // @ts-ignore
          if (json.features.length === 0) {
            return;
          }
          // @ts-ignore
          const r = json.features[0];
          // @ts-ignore
          this.popupInfoService.showTrafficIncidentInfo(
              new LngLat(r.geometry.x, r.geometry.y),
              {
                description: r.attributes.description,
                end_localtime: r.attributes.end_localtime,
                end_utctime: r.attributes.end_utctime,
                fulldescription: r.attributes.fulldescription,
                incidenttype: r.attributes.incidenttype,
                lastupdated_localtime: r.attributes.lastupdated_localtime,
                lastupdated_utctime: r.attributes.lastupdated_utctime,
                location: r.attributes.location,
                objectid: r.attributes.objectid,
                severity: r.attributes.severity,
                start_localtime: r.attributes.start_localtime,
                start_utctime: r.attributes.start_utctime,
              },
          );
        });
      }
    });
  }

  private checkPropagation(e: MapMouseEvent): boolean {
    if (this.handledClickEvents.has(e.originalEvent)) {
      return true;
    }
    this.handledClickEvents.add(e.originalEvent);
    return false;
  }

  private checkClickPropagation(e: MapMouseEvent): boolean {
    if (this.checkPropagation(e)) {
      return true;
    }
    if (this.observationsManagerService.observationBeingEdited()) {
      this.observationsManagerService.updateObservationLocation(e.lngLat);
      return true; // swallow the click, no other handler should act on it
    }
    return false;
  }

  private handleRoadStatusLayerClick(e: any) {
    if (this.checkClickPropagation(e)) {
      return;
    }
    const roadSegmentId = e.features[0]?.properties?.id as number;
    if (!!roadSegmentId) {
      this.popupInfoService.showRoadStatusInfoWindow(roadSegmentId, e.lngLat);
    }
  }

  private handleBreadCrumbSearchClick(event: MapMouseEvent, activeShift: boolean) {
    if (this.checkClickPropagation(event)) {
      return;
    }
    // if active shift track clicked then look for all tenant period, otherwise limit search by browser user filter
    const lookBackPeriod = activeShift ? this.defaultHoursAgoFilter : this.hoursAgoFilter;

    // credits to https://gis.stackexchange.com/questions/7430/what-ratio-scales-do-google-maps-zoom-levels-correspond-to
    const metersPerPx = 156543.03392 * Math.cos(event.lngLat.lat * Math.PI / 180) / Math.pow(2, this.map.getZoom());
    const searchParams = {
      coordinate: {
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng
      },
      timeFrom: Math.floor((new Date().valueOf() - lookBackPeriod * 60 * 60 * 1000) / 1000),
      timeTo: undefined,
      radius: Math.min(metersPerPx * 15, 5000), // toleration 15 pixels. maximum 5km because of ws limitation
      locationSources: !!this.vehicleDetailMode
        ? [+this.vehicleDetailMode]
        : (!!this.vehicleIdsFilter && this.vehicleIdsFilter.length > 0 ? this.vehicleIdsFilter : undefined),
      sessions: undefined,
      searchLimit: 50,
    } as LocationSearchParams;

    this.showSearchSnackBar('Searching for nearest breadcrumbs...');
    const that = this;
    this.locationApi.search(searchParams)
      .then(response => {
          const features = response.features;
          // if Activity filter is set, filter out the features
          let filteredOutByActivity = false;
          if (!!this.activityFilter && this.activityFilter !== ActivityFilter.NONE) {
            // iterate on features copy
            features.slice(0).forEach(feature => {
              // iterate on bit numbers for the selected activity filter
              // and check if any input was turned on
              if (!ActivityFilter.flagCheck(this.activityFilter, feature.properties.flags)) {
                features.splice(features.indexOf(feature), 1);
                filteredOutByActivity = true;
              }
            });
          }
          if (filteredOutByActivity) {
            console.log('Some breadcrumbs filtered out by activity filter.');
          }
          if (features.length === 0) {
            const message = 'No breadcrumbs found.';
            console.log(message);
            that.showSearchSnackBar(message);
          } else {
            console.log(`Found ${features.length} breadcrumb(s).`);
            that.selectBreadcrumbs(features as PointFeature[], 'map-viewer');
            this.hideSearchSnackBar();
          }
        }, (error) => {
          console.error('Breadcrumbs search failed.', error);
          that.showSearchSnackBar('Breadcrumbs search failed.');
        }
      );
  }

  private selectBreadcrumbs(pointFeatures: Array<PointFeature>, source: string) {
    const vehicleBreadcrumbs = pointFeatures.map((pointFeature) => {
      const vehicleId = pointFeature.properties.locationsourceid;
      const vehicle = this.vehiclesManager.getVehicle(vehicleId);

      if (vehicle) {
        const location = {
          id: pointFeature.properties.id,
          longitude: pointFeature.geometry.coordinates[0],
          latitude: pointFeature.geometry.coordinates[1],
          speed: pointFeature.properties.speed,
          heading: pointFeature.properties.heading,
          flags: pointFeature.properties.flags,
          gpsSource: String(pointFeature.properties.gpssource),
        } as BackendLocationModel;
        return new VehicleBreadcrumb(
          new Date(pointFeature.properties.unixtime * 1000),
          vehicle,
          null,
          location,
          pointFeature.properties.sessionkey,
          null,
        );
      } else {
        console.warn('No vehicle found for breadcrumb with locationSourceId=', vehicleId);
        return null;
      }
    }).filter(breadcrumb => !!breadcrumb);

    this.breadcrumbManagerService.selectBreadcrumb(vehicleBreadcrumbs, source);
  }

  private hideSearchSnackBar() {
    if (this.searchSnackbar != null) {
      this.searchSnackbar.dismiss();
      this.searchSnackbar = null;
    }
  }

  private showSearchSnackBar(message: string) {
    this.hideSearchSnackBar();
    this.searchSnackbar = this.toast.short(message);
  }

  private handleAssetsChanged(assets: Asset[]) {
    this.vehicleIdsFilter = assets.map(asset => asset.id);
  }

  private showPointCursorForLayer(layerId: string) {
    const that = this;
    that.map.on('mouseenter', layerId, () => {
      if (that.observationPlacementModeActive) {
        return;
      }
      that.map.getCanvas().style.cursor = 'pointer';
    });
    that.map.on('mouseleave', layerId, () => {
      if (that.observationPlacementModeActive) {
        return;
      }
      that.map.getCanvas().style.cursor = '';
    });
  }
}
